/**
 * PayrollPayRun — the Payroll section's v1 screen: the PAY-RUN VIEW over
 * GET /v1/payroll/pay-run (PR 168; table 0080). Pick a tax year and a period
 * number, see every employee with a CURRENT recorded row for that period,
 * ordered by employee number, each line carrying its per-period figures and
 * its derived year-to-date pair set against the stored FPS echoes (the stale
 * signal — see the YTD cells below).
 *
 * The RECORD action (PR 170 commit + PR 172 preview) hangs off this screen as
 * PayrollRecordPanel — a preview-then-commit gate, since a recorded period is
 * permanent. The CORRECTION action (PR 175) hangs off each ROW as
 * PayrollCorrectionPanel: a correction targets one existing period, so its
 * entry point carries its target. Still out of scope, deliberately: batch or
 * run-level state, payslips, and reading a superseded row's figures — no read
 * route returns them (see isCorrected).
 *
 * NULL IS NOT ZERO (the NI panel's standing warning): an NI-only row carries
 * no PAYE figure, no tax code, no stored echoes and no loan figures — those
 * cells render an em dash, never £0.00, and nothing here coerces with `?? 0`.
 * A £0.00 that does render is a REAL zero (e.g. a student-loan plan below
 * its threshold). Negatives are refunds, rendered in the accounting form
 * with a true minus sign (U+2212) — the Edna refund precedent.
 *
 * NET is the one column that is DERIVED, not stored: the server computes it
 * from the row's own figures (ERA 1996 s.8(2)(c) — gross less employee NI,
 * PAYE and the two loans) and ships it as netPence, so this screen only
 * formats it. Nothing here re-derives it, deliberately: one server-side
 * implementation is what keeps this table, the payslip to come and every
 * future consumer from disagreeing about a worker's take-home. It is also
 * the exception to the em-dash rule above — netPence is never absent, so an
 * NI-only row shows a real net rather than a dash.
 *
 * Page shape mirrors HRProviders (closr-table list in a ClosrCard) with the
 * controls held OUTSIDE the loading conditional, the NIPreviewSection rule:
 * an input surface must not unmount while the answer it drives refetches.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight, PenLine, Plus } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchPayRun, fetchDirectory, fetchPayslips } from '../../api'
import type { PayrollLedgerRow, Payslip } from '../../types'
import { money, signedMoney, penceCell } from '../../../../lib/money'
import PayrollRecordPanel from './PayrollRecordPanel'
import PayrollCorrectionPanel from './PayrollCorrectionPanel'
import PayslipGate, { PayslipCell } from './PayslipGate'
import PayslipViewer from './PayslipViewer'
import { DeductionList } from './DeductionFields'
import { useWriteAccess } from '../../../../lib/writeAccess'

// The registered rulesets, oldest first. 2026-27 is the ONLY tax year the
// SHIPPED backend holds tables for — the select renders exactly this list,
// because a hand-lengthened list would promise years no data can exist in.
// (The SYNTHETIC-TEST mechanism probe is a backend TEST-SUITE construction
// and never ships; it must never appear here.) The real 2027-28 slice adds
// its year when HMRC publishes (~Feb–Mar 2027).
const REGISTERED_TAX_YEARS = ['2026-27'] as const

// The page's default year — kept as an explicit named constant (not "last
// list entry") so a future second entry can never silently become the
// landing view; the real-year slice updates both consts together.
const DEFAULT_TAX_YEAR = '2026-27'

// A pay run spans employees of MIXED pay frequencies, so the per-frequency
// bounds the employee panel enforces (52 tax weeks plus that frequency's own
// extra payday week — 53 weekly, 54 two-weekly, 56 four-weekly — or 12 tax
// months) cannot apply here: there is no single frequency to bound by. The
// input therefore admits the schema's full 1..56 range and the server's own
// named 400 judges anything a specific frequency cannot reach.
const PERIOD_MIN = 1
const PERIOD_MAX = 56

// Wire tax_basis → display label; an unknown value renders verbatim rather
// than being hidden.
const BASIS_LABEL: Record<string, string> = {
  cumulative: 'Cumulative', week1_month1: 'W1/M1',
}

// The stale signal, judged per figure and only where a stored echo EXISTS:
// an NI-only row carries no echoes, and no echo means nothing to disagree
// with — which is exactly why the wire ships the two figures rather than a
// precomputed boolean. Stored == derived is the quiet normal; a mismatch
// means a correction was recorded upstream after this row was written.
function isRestated(r: PayrollLedgerRow): boolean {
  const payDiverges = r.payePayToDatePence !== undefined && r.payePayToDatePence !== r.derivedPayToDatePence
  const taxDiverges = r.payeTaxToDatePence !== undefined && r.payeTaxToDatePence !== r.derivedTaxToDatePence
  return payDiverges || taxDiverges
}

// A DIFFERENT FACT from isRestated above, and the two must not be conflated.
// "Restated" means an upstream correction moved THIS row's year-to-date after
// it was written (its stored echo disagrees with the derived sum). "Corrected"
// means this row IS a replacement — it superseded an earlier row for its own
// period, which the wire says by carrying `supersedes` (PR 175 restatement;
// the pay-run read already selects the column, so no new route is involved).
//
// The OLD figures are not reachable from here: every read filters
// `superseded_by IS NULL`, so a superseded row never comes back. The mark can
// therefore say THAT a period was corrected and not what it used to say —
// stating that plainly beats implying a history this screen cannot fetch.
function isCorrected(r: PayrollLedgerRow): boolean {
  return r.supersedes !== undefined && r.supersedes !== ''
}

// DEDUCTIONS AND THE EM-DASH RULE — the decision, and why it goes the other way
// from every optional figure on this table.
//
// The rule above says an ABSENT figure renders an em dash and never £0.00. A
// row with no deductions is not that case. `deductions` is not optional on the
// wire and every read path initialises it to [] rather than null, precisely so
// that "nothing was deducted under any other head" is expressible as an ANSWER
// instead of as a gap. otherDeductionsTotalPence is likewise a plain int64 with
// no absent state — a row with no deductions carries 0, and that 0 is the same
// kind of fact as a £0.00 student loan below its threshold, which this table
// already renders as a real zero.
//
// So an empty list renders £0.00. An em dash here would claim the server does
// not know what was deducted, when it knows exactly: nothing. That is the
// stronger, more specific statement, and it is the true one.
//
// ITEMISATION IS NOT IN THE CELL. The s.8(2)(b) particulars are amount AND
// purpose per line, which is prose, and prose does not fit a numeric column
// without either truncating (losing the particular that gives the figure
// meaning) or wrecking the table's alignment for every row that has none. An
// EXPANDABLE ROW was chosen over a tooltip or a drawer: a tooltip cannot be
// reached by keyboard or read by a screen reader reliably and cannot be
// compared across employees; a drawer covers the table the reader is scanning.
// The disclosure keeps the itemisation in the reading order, adjacent to the
// figure it explains, and lets two employees' lists be open at once.
function hasDeductions(r: PayrollLedgerRow): boolean {
  return r.deductions.length > 0
}

export default function PayrollPayRun() {
  // The newest REAL year by default (DEFAULT_TAX_YEAR) — deliberately not
  // "last list entry", which would have made the SYNTHETIC-TEST probe the
  // landing view the moment it registered.
  const [taxYear, setTaxYear] = useState<string>(DEFAULT_TAX_YEAR)
  const [period, setPeriod] = useState(1)
  const [periodInput, setPeriodInput] = useState('1')
  const [recording, setRecording] = useState(false)
  // The row being corrected, or null. A correction always targets one existing
  // row, so this holds the row itself rather than a boolean.
  const [correcting, setCorrecting] = useState<PayrollLedgerRow | null>(null)
  // The row whose payslip is being issued, and the payslip being viewed. Both
  // hold the subject rather than a boolean, for the same reason correcting does.
  const [issuing, setIssuing] = useState<PayrollLedgerRow | null>(null)
  const [viewing, setViewing] = useState<Payslip | null>(null)
  // Record, correct (restate) and payslip issue are records-level payroll
  // writes (lib/writeAccess; fail closed). Viewing a stored payslip stays.
  const { records } = useWriteAccess()
  // Bumped after a successful record so the pay run refetches — a new row must
  // not require a manual reload to appear.
  const [reloadKey, setReloadKey] = useState(0)
  // Row ids whose deduction itemisation is expanded. A SET rather than a single
  // id: two employees' lists should be comparable side by side, which is half
  // the reason this is an expandable row rather than a drawer.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  function toggleExpanded(id: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Applied on BLUR, the NI panel's contract: input the server would 400 on
  // (non-integer, outside 1..56) never leaves the browser — the field resets
  // to the last good value instead.
  function applyPeriod() {
    const v = Number(periodInput)
    if (!Number.isInteger(v) || v < PERIOD_MIN || v > PERIOD_MAX) {
      setPeriodInput(String(period))
      return
    }
    setPeriodInput(String(v))
    if (v !== period) setPeriod(v)
  }

  const { data, loading, error } = useAsync(() => fetchPayRun(taxYear, period), [taxYear, period, reloadKey])
  // The pay-run row carries no payslip field, so the two reads are correlated
  // HERE, on periodResultId — a fact the payslip list exposes, never inferred.
  const { data: payslips } = useAsync(() => fetchPayslips({ taxYear }), [taxYear, reloadKey])
  const payslipByRow = new Map((payslips ?? []).map(p => [p.periodResultId, p]))
  // The roster feeds the record panel's employee picker. Loaded with the page
  // rather than on open, so the picker is populated the moment it appears.
  const { data: employees } = useAsync(() => fetchDirectory(), [])

  const rows = data?.rows ?? []
  const anyRestated = rows.some(isRestated)
  const anyCorrected = rows.some(isCorrected)

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Pay run"
        subtitle={data
          ? `${rows.length} employee${rows.length !== 1 ? 's' : ''} recorded · tax year ${data.taxYear}, period ${data.periodNumber}`
          : undefined}
        action={!recording && records ? (
          <button onClick={() => setRecording(true)}
            className="k-btn k-btn--lg inline-flex items-center gap-2">
            <Plus size={14} /> Record a period
          </button>
        ) : undefined}
      />

      {recording && records && (
        <PayrollRecordPanel
          employees={employees ?? []}
          onClose={() => setRecording(false)}
          onRecorded={row => {
            // Move the table to the period the SERVER assigned the row, so the
            // new line is the one on screen, then refetch.
            if (row.taxYear !== taxYear) setTaxYear(row.taxYear)
            if (row.periodNumber !== period) {
              setPeriod(row.periodNumber)
              setPeriodInput(String(row.periodNumber))
            }
            setReloadKey(k => k + 1)
          }}
        />
      )}

      {issuing && records && (
        <div className="k-card">
          <PayslipGate
            row={issuing}
            onClose={() => setIssuing(null)}
            onIssued={() => setReloadKey(k => k + 1)}
          />
        </div>
      )}

      {viewing && (
        <div className="k-card">
          <PayslipViewer payslip={viewing} onClose={() => setViewing(null)} />
        </div>
      )}

      {correcting && records && (
        <PayrollCorrectionPanel
          row={correcting}
          onClose={() => setCorrecting(null)}
          onCorrected={() => {
            // The replacement lands in the SAME period the superseded row held
            // (a correction cannot move a payment between periods), so only a
            // refetch is needed — the table stays where it is and the new row
            // appears in place of the one it replaced.
            setReloadKey(k => k + 1)
          }}
        />
      )}

      {/* Controls — outside the loading conditional so they never unmount
          mid-refetch (the NIPreviewSection rule). */}
      <div className="flex items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-11 font-semibold uppercase tracking-overline text-ink-muted">Tax year</span>
          <select value={taxYear} aria-label="Tax year" onChange={e => setTaxYear(e.target.value)}
            className="h-8 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent">
            {REGISTERED_TAX_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
            Period <span className="font-normal normal-case tracking-normal text-ink-muted">({PERIOD_MIN}–{PERIOD_MAX})</span>
          </span>
          <input type="number" min={PERIOD_MIN} max={PERIOD_MAX} step={1} value={periodInput}
            aria-label="Period number"
            onChange={e => setPeriodInput(e.target.value)}
            onBlur={applyPeriod}
            className="w-24 h-8 px-2 text-13 text-right tabular-nums bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
        </label>
      </div>

      {error ? (
        <ErrorState message={error} />
      ) : loading || !data ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <div className="k-card">
          {/* 200 with rows: [] is the COMMON case, not an error: a pay run
              that has not happened yet — or a tax year nothing was recorded
              in — is a legitimate state. Never "not found". */}
          <EmptyState
            message={`The pay run for period ${data.periodNumber} hasn't been recorded yet`}
            hint={`Nothing is recorded for tax year ${data.taxYear}, period ${data.periodNumber}. When it is, every employee with a result for the period appears here.`}
          />
        </div>
      ) : (
        <div className="k-card">
          <div className="overflow-x-auto">
            <table className="closr-table dash-table w-full">
              <thead>
                <tr>
                  <th className="text-left">#</th>
                  <th className="text-left">Employee</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Employee NI</th>
                  <th className="text-right">Employer NI</th>
                  <th className="text-right">PAYE tax</th>
                  <th className="text-right">Student loan</th>
                  <th className="text-right">PGL</th>
                  <th className="text-right">Deductions</th>
                  <th className="text-right">Net</th>
                  <th className="text-left">Code</th>
                  <th className="text-left">Basis</th>
                  <th className="text-right">YTD pay</th>
                  <th className="text-right">YTD tax</th>
                  <th className="text-left">Payslip</th>
                  <th className="text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.flatMap(r => {
                  const restated = isRestated(r)
                  const corrected = isCorrected(r)
                  const itemised = hasDeductions(r)
                  const isOpen = expanded.has(r.id)
                  return [
                    <tr key={r.id}>
                      <td className="text-12 text-ink-secondary whitespace-nowrap">{r.employeeNumber}</td>
                      <td>
                        <span className="text-13 font-medium text-ink-primary">{r.fullName}</span>
                        {/* The visible row mark — words, not a colour: the
                            theme's palette carries meaning elsewhere and no
                            token exists for "restated"; the footnote below
                            the table says what the word means. */}
                        {restated && (
                          <span className="ml-2 text-11 font-semibold uppercase tracking-overline text-ink-secondary">restated</span>
                        )}
                        {/* A row that IS a replacement — see isCorrected. Same
                            words-not-colour rule as the mark above. */}
                        {corrected && (
                          <span className="ml-2 text-11 font-semibold uppercase tracking-overline text-ink-secondary">corrected</span>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{signedMoney(r.grossPence)}</td>
                      <td className="text-right tabular-nums">{penceCell(r.niEmployeePence)}</td>
                      <td className="text-right tabular-nums">{penceCell(r.niEmployerPence)}</td>
                      <td className="text-right tabular-nums">{penceCell(r.payeTaxPence)}</td>
                      <td className="text-right tabular-nums">{penceCell(r.studentLoanPence)}</td>
                      <td className="text-right tabular-nums">{penceCell(r.postgraduateLoanPence)}</td>
                      {/* Deductions — money, NOT penceCell: see the decision
                          above hasDeductions. An empty list is a real zero, so
                          this figure never renders an em dash. The disclosure
                          appears only where there is something to disclose;
                          a £0.00 with a chevron would promise an empty list. */}
                      <td className="text-right tabular-nums">
                        {itemised ? (
                          <button onClick={() => toggleExpanded(r.id)}
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Hide' : 'Show'} the ${r.deductions.length} deduction${r.deductions.length !== 1 ? 's' : ''} for ${r.fullName}`}
                            className="inline-flex items-center gap-1 tabular-nums text-ink-body hover:text-ink-primary transition-colors">
                            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            {money(r.otherDeductionsTotalPence / 100)}
                          </button>
                        ) : (
                          money(r.otherDeductionsTotalPence / 100)
                        )}
                      </td>
                      {/* Net — the one figure on this table that is DERIVED
                          rather than stored (server-side, once: see the
                          block comment). It is the exception to the em-dash
                          rule above: netPence is never absent, so it uses
                          signedMoney directly rather than penceCell — an
                          NI-only row has a real net, and rendering it as an
                          em dash would claim an absence that does not exist.
                          signedMoney, not money: a net cannot go negative
                          from these four deductions alone, but a correction
                          could produce one, and it should read as a figure
                          rather than as a typo if it ever does. */}
                      <td className="text-right tabular-nums font-medium text-ink-primary">{signedMoney(r.netPence)}</td>
                      <td className="text-12 text-ink-secondary whitespace-nowrap">{r.taxCode ?? '—'}</td>
                      <td className="text-12 text-ink-secondary whitespace-nowrap">{BASIS_LABEL[r.taxBasis] ?? r.taxBasis}</td>
                      {/* The derived pair is what renders — the ledger's
                          carry-forward truth. When a stored echo diverges,
                          the reported-at-the-time figure appears beneath it;
                          when they agree, the derived figure stands alone. */}
                      <td className="text-right">
                        <div className="tabular-nums">{money(r.derivedPayToDatePence / 100)}</div>
                        {r.payePayToDatePence !== undefined && r.payePayToDatePence !== r.derivedPayToDatePence && (
                          <div className="text-11 text-ink-muted tabular-nums whitespace-nowrap">reported {money(r.payePayToDatePence / 100)}</div>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="tabular-nums">{r.derivedTaxToDatePence === undefined ? '—' : signedMoney(r.derivedTaxToDatePence)}</div>
                        {r.payeTaxToDatePence !== undefined && r.payeTaxToDatePence !== r.derivedTaxToDatePence && (
                          <div className="text-11 text-ink-muted tabular-nums whitespace-nowrap">reported {signedMoney(r.payeTaxToDatePence)}</div>
                        )}
                      </td>
                      {/* The payslip cell. Issued -> view the STORED bytes;
                          current and unissued -> the issue gate; superseded ->
                          no action at all, because that version can never
                          produce a new statement. */}
                      <td className="text-left">
                        <PayslipCell row={r} payslip={payslipByRow.get(r.id)}
                          onIssue={records ? setIssuing : undefined} onView={setViewing} />
                      </td>
                      {/* The correction entry point lives ON THE ROW: a
                          correction targets one existing period, so the action
                          carries its target rather than opening a form that
                          would have to name one. */}
                      <td className="text-right">
                        {records && (
                          <button onClick={() => setCorrecting(r)}
                            aria-label={`Correct period ${r.periodNumber} for ${r.fullName}`}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover whitespace-nowrap">
                            <PenLine size={12} /> Correct
                          </button>
                        )}
                      </td>
                    </tr>,
                    // The itemisation, in the reading order directly beneath
                    // the figure it explains. Rendered only when open, so a
                    // collapsed table is byte-for-byte the table that existed
                    // before deductions.
                    ...(itemised && isOpen ? [(
                      <tr key={`${r.id}-deductions`}>
                        <td colSpan={15} className="px-4 py-3 bg-surface-nav-hover">
                          <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">
                            {r.fullName} — deductions
                          </div>
                          <DeductionList deductions={r.deductions} />
                        </td>
                      </tr>
                    )] : []),
                  ]
                })}
              </tbody>
            </table>
          </div>
          {anyCorrected && (
            <div className="text-12 text-ink-muted leading-relaxed px-4 py-3 border-t border-edge-row-faint">
              Rows marked “corrected”: this row replaced an earlier one for the same period, which
              is kept as superseded history. The figures shown are the ones in force; the superseded
              row’s figures are not available on this screen. That is a different mark from
              “restated”, which says a correction elsewhere moved this row’s year-to-date total.
            </div>
          )}
          {anyRestated && (
            <div className="text-12 text-ink-muted leading-relaxed px-4 py-3 border-t border-edge-row-faint">
              Rows marked “restated”: the reported figure is the stored year-to-date total — what
              was reported at the time the row was written. The year-to-date figure shown is derived
              from the rows as they stand now, so it reflects corrections recorded since. Everywhere
              else the two agree, and the derived figure stands alone.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
